import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Settings, CreditCard, MessageSquare, Building2, Users, Plus,
  Save, Loader2, Eye, EyeOff, BarChart3, Pencil, Trash2, Key, User, Mail, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';

export default function SuperAdminDashboard() {
  const { user, login } = useAuth();
  const [communities, setCommunities] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});

  // Profile edit state
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Razorpay config
  const [razorpayKeyId, setRazorpayKeyId] = useState('');
  const [razorpayKeySecret, setRazorpayKeySecret] = useState('');
  const [razorpayWebhookSecret, setRazorpayWebhookSecret] = useState('');

  // OTP config
  const [otpEmailEnabled, setOtpEmailEnabled] = useState(false);
  const [otpSmsEnabled, setOtpSmsEnabled] = useState(false);
  const [smsProvider, setSmsProvider] = useState('none');
  const [smsApiKey, setSmsApiKey] = useState('');
  const [smsApiSecret, setSmsApiSecret] = useState('');
  const [smsFromPhone, setSmsFromPhone] = useState('');

  // SMTP config
  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [smtpProvider, setSmtpProvider] = useState('brevo');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFromEmail, setSmtpFromEmail] = useState('');

  // Community dialogs
  const [showNewCommunity, setShowNewCommunity] = useState(false);
  const [editCommunity, setEditCommunity] = useState(null);
  const [showAssignAdmin, setShowAssignAdmin] = useState(null);
  const [deleteCommunity, setDeleteCommunity] = useState(null);

  // User dialogs
  const [showNewUser, setShowNewUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetPassword, setResetPassword] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Locked periods modal
  const [lockedPeriodsModal, setLockedPeriodsModal] = useState({ open: false, community: null });
  const [lockedPeriods, setLockedPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [settingsRes, commRes, usersRes, statsRes] = await Promise.all([
        api.get('/superadmin/settings'),
        api.get('/superadmin/communities'),
        api.get('/superadmin/users'),
        api.get('/superadmin/dashboard')
      ]);
      setCommunities(commRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);

      const rz = settingsRes.data.razorpay_config || {};
      setRazorpayKeyId(rz.key_id || '');
      setRazorpayKeySecret(rz.key_secret || '');
      setRazorpayWebhookSecret(rz.webhook_secret || '');

      const otp = settingsRes.data.otp_config || {};
      setOtpEmailEnabled(otp.email_enabled || false);
      setOtpSmsEnabled(otp.sms_enabled || false);
      setSmsProvider(otp.sms_provider || 'none');
      setSmsApiKey(otp.sms_api_key || '');
      setSmsApiSecret(otp.sms_api_secret || '');
      setSmsFromPhone(otp.sms_from_phone || '');

      const smtp = settingsRes.data.smtp_config || {};
      setSmtpEnabled(smtp.enabled || false);
      setSmtpProvider(smtp.provider || 'brevo');
      setSmtpHost(smtp.smtp_host || '');
      setSmtpPort(smtp.smtp_port || '587');
      setSmtpUser(smtp.smtp_user || '');
      setSmtpPassword(smtp.smtp_password || '');
      setSmtpFromEmail(smtp.from_email || '');
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load profile data from user context
  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
      setProfileEmail(user.email || '');
    }
  }, [user]);

  const saveProfile = async () => {
    // Validate
    if (newPassword && newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword && !currentPassword) {
      toast.error('Enter current password to change password');
      return;
    }
    
    setSavingProfile(true);
    try {
      const payload = {};
      if (profileName !== user?.name) payload.name = profileName;
      if (profileEmail !== user?.email) payload.email = profileEmail;
      if (newPassword) {
        payload.current_password = currentPassword;
        payload.new_password = newPassword;
      }
      
      const res = await api.put('/superadmin/profile', payload);
      
      // Update local auth state if email changed
      if (res.data.user) {
        const token = localStorage.getItem('token');
        login(token, res.data.user);
      }
      
      toast.success('Profile updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/settings', {
        settings: {
          razorpay_config: { key_id: razorpayKeyId, key_secret: razorpayKeySecret, webhook_secret: razorpayWebhookSecret },
          otp_config: { 
            email_enabled: otpEmailEnabled, 
            sms_enabled: otpSmsEnabled,
            sms_provider: smsProvider,
            sms_api_key: smsApiKey,
            sms_api_secret: smsApiSecret,
            sms_from_phone: smsFromPhone
          },
          smtp_config: { 
            enabled: smtpEnabled, 
            provider: smtpProvider, 
            smtp_host: smtpHost, 
            smtp_port: smtpPort, 
            smtp_user: smtpUser, 
            smtp_password: smtpPassword, 
            from_email: smtpFromEmail 
          }
        }
      });
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // ============ COMMUNITY CRUD ============
  const handleCreateCommunity = async () => {
    if (!formData.name || !formData.total_units) { toast.error('Name and total units required'); return; }
    setFormLoading(true);
    try {
      await api.post('/superadmin/communities', formData);
      toast.success('Community created');
      setShowNewCommunity(false);
      setFormData({});
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleUpdateCommunity = async () => {
    setFormLoading(true);
    try {
      await api.put(`/superadmin/communities/${editCommunity.community_id}`, formData);
      toast.success('Community updated');
      setEditCommunity(null);
      setFormData({});
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleDeleteCommunity = async () => {
    setFormLoading(true);
    try {
      await api.delete(`/superadmin/communities/${deleteCommunity.community_id}`);
      toast.success('Community deleted');
      setDeleteCommunity(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleAssignAdmin = async () => {
    if (!formData.admin_email || !formData.admin_name) { toast.error('Admin name and email required'); return; }
    setFormLoading(true);
    try {
      await api.post('/superadmin/communities/assign-admin', {
        community_id: showAssignAdmin.community_id,
        admin_email: formData.admin_email,
        admin_name: formData.admin_name,
        admin_password: formData.admin_password || 'admin123'
      });
      toast.success(`Admin assigned to ${showAssignAdmin.name}`);
      setShowAssignAdmin(null);
      setFormData({});
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  // ============ LOCKED PERIODS MANAGEMENT ============
  const loadLockedPeriods = async (community) => {
    setLockedPeriodsModal({ open: true, community });
    setLoadingPeriods(true);
    try {
      const res = await api.get(`/superadmin/communities/${community.community_id}/locked-periods`);
      setLockedPeriods(res.data || []);
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to load locked periods'); 
      setLockedPeriods([]);
    }
    finally { setLoadingPeriods(false); }
  };

  const handleUnlockPeriod = async (monthLabel) => {
    const community = lockedPeriodsModal.community;
    if (!community) return;
    
    if (!window.confirm(`Unlock "${monthLabel}" for ${community.name}? Community admin will be able to edit/delete this period.`)) return;
    
    setFormLoading(true);
    try {
      await api.post('/admin/maintenance/unlock-period', {
        month_label: monthLabel,
        community_id: community.community_id
      });
      toast.success(`${monthLabel} unlocked for ${community.name}`);
      setLockedPeriods(prev => prev.filter(p => p.month_label !== monthLabel));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unlock'); }
    finally { setFormLoading(false); }
  };

  // ============ USER CRUD ============
  const handleCreateUser = async () => {
    if (!formData.email || !formData.name) { toast.error('Name and email required'); return; }
    setFormLoading(true);
    try {
      await api.post('/superadmin/users', formData);
      toast.success('User created');
      setShowNewUser(false);
      setFormData({});
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleUpdateUser = async () => {
    setFormLoading(true);
    try {
      await api.put(`/superadmin/users/${editUser.user_id}`, formData);
      toast.success('User updated');
      setEditUser(null);
      setFormData({});
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!formData.password || formData.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setFormLoading(true);
    try {
      await api.put(`/superadmin/users/${resetPassword.user_id}/password`, { password: formData.password });
      toast.success('Password updated');
      setResetPassword(null);
      setFormData({});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleDeleteUser = async () => {
    setFormLoading(true);
    try {
      await api.delete(`/superadmin/users/${deleteUser.user_id}`);
      toast.success('User deleted');
      setDeleteUser(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const toggleSecret = (key) => setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="superadmin-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Platform Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Configure integrations, manage communities and users</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Users', value: stats.total_users || 0, icon: Users, color: 'bg-blue-50 text-blue-600' },
          { label: 'Communities', value: stats.total_communities || 0, icon: Building2, color: 'bg-emerald-50 text-emerald-600' },
          { label: 'Payments', value: stats.total_payments || 0, icon: CreditCard, color: 'bg-orange-50 text-orange-600' },
          { label: 'Revenue', value: `₹${(stats.total_revenue || 0).toLocaleString()}`, icon: BarChart3, color: 'bg-violet-50 text-violet-600' }
        ].map((s, i) => (
          <Card key={i} className="border-slate-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}><s.icon size={18} /></div>
              <div><p className="text-xs text-slate-500">{s.label}</p><p className="text-lg font-bold text-slate-900">{s.value}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="communities" className="w-full">
        <TabsList className="bg-slate-100 mb-6">
          <TabsTrigger value="communities" className="text-xs"><Building2 size={14} className="mr-1.5" /> Communities</TabsTrigger>
          <TabsTrigger value="users" className="text-xs"><Users size={14} className="mr-1.5" /> Users</TabsTrigger>
          <TabsTrigger value="integrations" className="text-xs"><Settings size={14} className="mr-1.5" /> Integrations</TabsTrigger>
          <TabsTrigger value="profile" className="text-xs"><User size={14} className="mr-1.5" /> My Profile</TabsTrigger>
        </TabsList>

        {/* Communities Tab */}
        <TabsContent value="communities">
          <Card className="border-slate-100">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-['Outfit']">Communities</CardTitle>
                <CardDescription className="text-xs">{communities.length} communities</CardDescription>
              </div>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => { setFormData({ type: 'apartment' }); setShowNewCommunity(true); }}>
                <Plus size={14} /> Add Community
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Units</TableHead>
                    <TableHead className="text-xs">Admin</TableHead>
                    <TableHead className="text-xs">Residents</TableHead>
                    <TableHead className="text-xs w-[180px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {communities.map(c => (
                    <TableRow key={c.community_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-slate-400 truncate max-w-[200px]">{c.address}</p>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs capitalize">{c.type?.replace('_', ' ')}</Badge></TableCell>
                      <TableCell className="text-sm"><span className="font-medium">{c.registered_units || 0}</span><span className="text-slate-400">/{c.total_units}</span></TableCell>
                      <TableCell>
                        {c.admin_email ? (
                          <div><p className="text-xs font-medium">{c.admin_name}</p><p className="text-[10px] text-slate-400">{c.admin_email}</p></div>
                        ) : <span className="text-xs text-slate-400">Not assigned</span>}
                      </TableCell>
                      <TableCell className="text-sm font-medium">{c.resident_count || 0}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => loadLockedPeriods(c)} title="Manage Locked Periods">
                            <Unlock size={14} className="text-amber-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setFormData({ admin_email: c.admin_email || '', admin_name: c.admin_name || '', admin_password: 'admin123' }); setShowAssignAdmin(c); }} title="Assign Admin">
                            <Users size={14} className="text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setFormData({ name: c.name, address: c.address, type: c.type, total_units: c.total_units }); setEditCommunity(c); }} title="Edit Community">
                            <Pencil size={14} className="text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteCommunity(c)} title="Delete Community">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="border-slate-100">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-['Outfit']">All Users</CardTitle>
                <CardDescription className="text-xs">{users.length} users across all communities</CardDescription>
              </div>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => { setFormData({ role: 'resident' }); setShowNewUser(true); }}>
                <Plus size={14} /> Add User
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Email</TableHead>
                    <TableHead className="text-xs">Role</TableHead>
                    <TableHead className="text-xs">Community</TableHead>
                    <TableHead className="text-xs">Joined</TableHead>
                    <TableHead className="text-xs w-[140px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.user_id}>
                      <TableCell className="font-medium text-sm">{u.name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${u.role === 'super_admin' ? 'bg-red-50 text-red-700' : u.role === 'admin' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                          {u.role?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{u.community_name || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-500">{u.created_at?.split('T')[0]}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setFormData({ password: '' }); setResetPassword(u); }} title="Reset Password">
                            <Key size={14} className="text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setFormData({ name: u.name, email: u.email, phone: u.phone, role: u.role, community_id: u.community_id || '', unit_number: u.unit_number }); setEditUser(u); }}>
                            <Pencil size={14} className="text-slate-500" />
                          </Button>
                          {u.role !== 'super_admin' && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteUser(u)}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Razorpay Config */}
            <Card className="border-slate-100">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><CreditCard size={18} className="text-blue-600" /></div>
                  <div><CardTitle className="text-base font-['Outfit']">Razorpay</CardTitle><CardDescription className="text-xs">Payment gateway</CardDescription></div>
                  <Badge variant={razorpayKeyId ? "default" : "secondary"} className={`ml-auto text-xs ${razorpayKeyId ? 'bg-emerald-100 text-emerald-700' : ''}`}>{razorpayKeyId ? 'Configured' : 'Not Set'}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div><Label className="text-xs mb-1 block">Key ID</Label><Input value={razorpayKeyId} onChange={e => setRazorpayKeyId(e.target.value)} placeholder="rzp_test_..." className="text-sm" /></div>
                <div>
                  <Label className="text-xs mb-1 block">Key Secret</Label>
                  <div className="relative">
                    <Input type={showSecrets.rzSecret ? 'text' : 'password'} value={razorpayKeySecret} onChange={e => setRazorpayKeySecret(e.target.value)} placeholder="Enter secret" className="text-sm pr-10" />
                    <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => toggleSecret('rzSecret')}>{showSecrets.rzSecret ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                  </div>
                </div>
                <div><Label className="text-xs mb-1 block">Webhook Secret</Label><Input type="password" value={razorpayWebhookSecret} onChange={e => setRazorpayWebhookSecret(e.target.value)} placeholder="Optional" className="text-sm" /></div>
              </CardContent>
            </Card>

            {/* OTP Configuration */}
            <Card className="border-slate-100 lg:col-span-2">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center"><MessageSquare size={18} className="text-orange-600" /></div>
                  <div><CardTitle className="text-base font-['Outfit']">OTP Delivery Channels</CardTitle><CardDescription className="text-xs">Configure how OTP codes are sent to users</CardDescription></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Email OTP */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <input type="checkbox" id="otp-email-enabled" checked={otpEmailEnabled} onChange={e => setOtpEmailEnabled(e.target.checked)} className="w-4 h-4 text-emerald-600 rounded" />
                      <Label htmlFor="otp-email-enabled" className="text-sm font-medium cursor-pointer">Email OTP</Label>
                      <Badge className={`ml-auto text-xs ${otpEmailEnabled && smtpEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {otpEmailEnabled && smtpEnabled ? 'Active' : otpEmailEnabled && !smtpEnabled ? 'SMTP Required' : 'Disabled'}
                      </Badge>
                    </div>
                    {otpEmailEnabled && (
                      <div className={`text-xs p-3 rounded-lg ${smtpEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {smtpEnabled ? (
                          <p>✓ Will use your configured SMTP ({smtpProvider}) to send OTP emails</p>
                        ) : (
                          <p>⚠ Please configure SMTP settings below to enable email OTP</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* SMS OTP */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <input type="checkbox" id="otp-sms-enabled" checked={otpSmsEnabled} onChange={e => setOtpSmsEnabled(e.target.checked)} className="w-4 h-4 text-emerald-600 rounded" />
                      <Label htmlFor="otp-sms-enabled" className="text-sm font-medium cursor-pointer">SMS OTP</Label>
                      <Badge className={`ml-auto text-xs ${otpSmsEnabled && smsProvider !== 'none' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {otpSmsEnabled && smsProvider !== 'none' ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                    {otpSmsEnabled && (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs mb-1 block">SMS Provider</Label>
                          <Select value={smsProvider} onValueChange={setSmsProvider}>
                            <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select Provider</SelectItem>
                              <SelectItem value="twilio">Twilio</SelectItem>
                              <SelectItem value="msg91">MSG91</SelectItem>
                              <SelectItem value="nexmo">Vonage (Nexmo)</SelectItem>
                              <SelectItem value="aws_sns">AWS SNS</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {smsProvider !== 'none' && (
                          <>
                            <div><Label className="text-xs mb-1 block">{smsProvider === 'twilio' ? 'Account SID' : 'API Key'}</Label><Input value={smsApiKey} onChange={e => setSmsApiKey(e.target.value)} placeholder="Enter API key" className="text-sm" /></div>
                            <div>
                              <Label className="text-xs mb-1 block">{smsProvider === 'twilio' ? 'Auth Token' : 'API Secret'}</Label>
                              <div className="relative">
                                <Input type={showSecrets.smsSecret ? 'text' : 'password'} value={smsApiSecret} onChange={e => setSmsApiSecret(e.target.value)} placeholder="Enter secret" className="text-sm pr-10" />
                                <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => toggleSecret('smsSecret')}>{showSecrets.smsSecret ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                              </div>
                            </div>
                            <div><Label className="text-xs mb-1 block">From Phone Number</Label><Input value={smsFromPhone} onChange={e => setSmsFromPhone(e.target.value)} placeholder="+1234567890" className="text-sm" /></div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Mock Mode Notice */}
                {!otpEmailEnabled && !otpSmsEnabled && (
                  <div className="mt-4 bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <p className="text-xs text-amber-700">⚠ No OTP channel enabled. OTP codes will be shown on screen for testing (Mock Mode).</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SMTP Email Config */}
            <Card className="border-slate-100 lg:col-span-2">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <div><CardTitle className="text-base font-['Outfit']">SMTP Email</CardTitle><CardDescription className="text-xs">Configure email sending for OTP and notifications</CardDescription></div>
                  <Badge variant="secondary" className={`ml-auto text-xs ${smtpEnabled ? 'bg-emerald-100 text-emerald-700' : ''}`}>{smtpEnabled ? 'Enabled' : 'Disabled'}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                  <input type="checkbox" id="smtp-enabled" checked={smtpEnabled} onChange={e => setSmtpEnabled(e.target.checked)} className="w-4 h-4 text-emerald-600 rounded" />
                  <Label htmlFor="smtp-enabled" className="text-sm cursor-pointer">Enable SMTP Email Sending</Label>
                </div>
                
                {smtpEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs mb-1 block">Provider</Label>
                      <Select value={smtpProvider} onValueChange={setSmtpProvider}>
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="brevo">Brevo (Sendinblue)</SelectItem>
                          <SelectItem value="gmail">Gmail SMTP</SelectItem>
                          <SelectItem value="outlook">Outlook / Microsoft 365</SelectItem>
                          <SelectItem value="amazon_ses">Amazon SES</SelectItem>
                          <SelectItem value="custom">Custom SMTP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {smtpProvider === 'custom' && (
                      <>
                        <div><Label className="text-xs mb-1 block">SMTP Host</Label><Input value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="smtp.example.com" className="text-sm" /></div>
                        <div><Label className="text-xs mb-1 block">SMTP Port</Label><Input value={smtpPort} onChange={e => setSmtpPort(e.target.value)} placeholder="587" className="text-sm" /></div>
                      </>
                    )}
                    
                    {smtpProvider === 'amazon_ses' && (
                      <div><Label className="text-xs mb-1 block">SES Region Host</Label><Input value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="email-smtp.us-east-1.amazonaws.com" className="text-sm" /></div>
                    )}
                    
                    <div>
                      <Label className="text-xs mb-1 block">{smtpProvider === 'brevo' ? 'Brevo Login Email' : 'SMTP Username'}</Label>
                      <Input value={smtpUser} onChange={e => setSmtpUser(e.target.value)} placeholder={smtpProvider === 'brevo' ? 'your-email@example.com' : 'username'} className="text-sm" />
                    </div>
                    
                    <div>
                      <Label className="text-xs mb-1 block">{smtpProvider === 'brevo' ? 'Brevo SMTP Key' : smtpProvider === 'gmail' ? 'App Password' : 'SMTP Password'}</Label>
                      <div className="relative">
                        <Input type={showSecrets.smtpPassword ? 'text' : 'password'} value={smtpPassword} onChange={e => setSmtpPassword(e.target.value)} placeholder="Enter password/key" className="text-sm pr-10" />
                        <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => toggleSecret('smtpPassword')}>{showSecrets.smtpPassword ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs mb-1 block">From Email Address</Label>
                      <Input value={smtpFromEmail} onChange={e => setSmtpFromEmail(e.target.value)} placeholder="noreply@yourcompany.com" className="text-sm" />
                    </div>
                  </div>
                )}
                
                {smtpEnabled && smtpProvider === 'brevo' && (
                  <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-xs text-blue-700 font-medium mb-1">Brevo Setup:</p>
                    <ol className="text-xs text-blue-600 list-decimal ml-4 space-y-1">
                      <li>Sign up at <a href="https://www.brevo.com" target="_blank" rel="noopener noreferrer" className="underline">brevo.com</a></li>
                      <li>Go to SMTP & API → SMTP → Generate SMTP Key</li>
                      <li>Use your Brevo login email as username</li>
                      <li>Use the generated SMTP Key as password</li>
                    </ol>
                  </div>
                )}
                
                {smtpEnabled && smtpProvider === 'gmail' && (
                  <div className="mt-4 bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <p className="text-xs text-amber-700 font-medium mb-1">Gmail Setup:</p>
                    <ol className="text-xs text-amber-600 list-decimal ml-4 space-y-1">
                      <li>Enable 2-Factor Authentication on your Google account</li>
                      <li>Go to Google Account → Security → App Passwords</li>
                      <li>Generate a new App Password for "Mail"</li>
                      <li>Use your Gmail address as username and App Password as password</li>
                    </ol>
                  </div>
                )}
                
                {!smtpEnabled && (
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-4 text-center">
                    <p className="text-sm text-slate-500">SMTP is disabled. OTP codes will be shown on screen for testing.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={saveSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 gap-2">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}Save Settings</Button>
          </div>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Account Info */}
            <Card className="border-slate-100">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <User size={18} className="text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-['Outfit']">Account Information</CardTitle>
                    <CardDescription className="text-xs">Update your name and email</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs mb-1.5 block">Full Name</Label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input 
                      value={profileName} 
                      onChange={e => setProfileName(e.target.value)} 
                      placeholder="Your name" 
                      className="pl-9"
                      data-testid="profile-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block">Email Address</Label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input 
                      type="email"
                      value={profileEmail} 
                      onChange={e => setProfileEmail(e.target.value)} 
                      placeholder="your@email.com" 
                      className="pl-9"
                      data-testid="profile-email-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="border-slate-100">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Lock size={18} className="text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-['Outfit']">Change Password</CardTitle>
                    <CardDescription className="text-xs">Update your login password</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs mb-1.5 block">Current Password</Label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input 
                      type="password"
                      value={currentPassword} 
                      onChange={e => setCurrentPassword(e.target.value)} 
                      placeholder="Enter current password" 
                      className="pl-9"
                      data-testid="current-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block">New Password</Label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input 
                      type="password"
                      value={newPassword} 
                      onChange={e => setNewPassword(e.target.value)} 
                      placeholder="Min 6 characters" 
                      className="pl-9"
                      data-testid="new-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block">Confirm New Password</Label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input 
                      type="password"
                      value={confirmPassword} 
                      onChange={e => setConfirmPassword(e.target.value)} 
                      placeholder="Re-enter new password" 
                      className="pl-9"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 flex justify-end">
            <Button 
              onClick={saveProfile} 
              disabled={savingProfile} 
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="save-profile-btn"
            >
              {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Profile
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* ===== DIALOGS ===== */}
      
      {/* New Community */}
      <Dialog open={showNewCommunity} onOpenChange={setShowNewCommunity}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">New Community</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs mb-1 block">Name *</Label><Input value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Sunrise Apartments" /></div>
            <div><Label className="text-xs mb-1 block">Address</Label><Input value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} placeholder="Full address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Type</Label>
                <Select value={formData.type || 'apartment'} onValueChange={v => setFormData({ ...formData, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="gated_community">Gated Community</SelectItem>
                    <SelectItem value="housing_society">Housing Society</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs mb-1 block">Total Units *</Label><Input type="number" value={formData.total_units || ''} onChange={e => setFormData({ ...formData, total_units: parseInt(e.target.value) || '' })} placeholder="100" /></div>
            </div>
            <Button onClick={handleCreateCommunity} disabled={formLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">{formLoading && <Loader2 size={14} className="animate-spin mr-2" />}Create Community</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Community */}
      <Dialog open={!!editCommunity} onOpenChange={(o) => { if (!o) setEditCommunity(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">Edit Community</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs mb-1 block">Name</Label><Input value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} /></div>
            <div><Label className="text-xs mb-1 block">Address</Label><Input value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Type</Label>
                <Select value={formData.type || ''} onValueChange={v => setFormData({ ...formData, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="gated_community">Gated Community</SelectItem>
                    <SelectItem value="housing_society">Housing Society</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs mb-1 block">Total Units</Label><Input type="number" value={formData.total_units || ''} onChange={e => setFormData({ ...formData, total_units: parseInt(e.target.value) || '' })} /></div>
            </div>
            <Button onClick={handleUpdateCommunity} disabled={formLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">{formLoading && <Loader2 size={14} className="animate-spin mr-2" />}Update</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Community */}
      <Dialog open={!!deleteCommunity} onOpenChange={(o) => { if (!o) setDeleteCommunity(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-red-600">Delete Community</DialogTitle>
            <DialogDescription>This will delete all units, tickets, announcements, and payments for this community. Users will be unassigned but not deleted.</DialogDescription>
          </DialogHeader>
          <div className="py-4"><p className="text-sm">Are you sure you want to delete <strong>{deleteCommunity?.name}</strong>?</p></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCommunity(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCommunity} disabled={formLoading}>{formLoading && <Loader2 size={14} className="animate-spin mr-2" />}Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Admin */}
      <Dialog open={!!showAssignAdmin} onOpenChange={(o) => { if (!o) setShowAssignAdmin(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">Assign Admin to {showAssignAdmin?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs mb-1 block">Admin Name *</Label><Input value={formData.admin_name || ''} onChange={e => setFormData({ ...formData, admin_name: e.target.value })} placeholder="Full name" /></div>
            <div><Label className="text-xs mb-1 block">Admin Email *</Label><Input type="email" value={formData.admin_email || ''} onChange={e => setFormData({ ...formData, admin_email: e.target.value })} placeholder="admin@example.com" /></div>
            <div>
              <Label className="text-xs mb-1 block">Password (for new user)</Label>
              <Input value={formData.admin_password || ''} onChange={e => setFormData({ ...formData, admin_password: e.target.value })} placeholder="admin123" />
              <p className="text-[10px] text-slate-400 mt-1">If email exists, user is promoted to admin. Otherwise, new account created.</p>
            </div>
            <Button onClick={handleAssignAdmin} disabled={formLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">{formLoading && <Loader2 size={14} className="animate-spin mr-2" />}Assign Admin</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New User */}
      <Dialog open={showNewUser} onOpenChange={setShowNewUser}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">New User</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs mb-1 block">Name *</Label><Input value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Full name" /></div>
            <div><Label className="text-xs mb-1 block">Email *</Label><Input type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="user@example.com" /></div>
            <div><Label className="text-xs mb-1 block">Phone</Label><Input value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Role</Label>
                <Select value={formData.role || 'resident'} onValueChange={v => setFormData({ ...formData, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resident">Resident</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Community</Label>
                <Select value={formData.community_id || 'none'} onValueChange={v => setFormData({ ...formData, community_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {communities.map(c => <SelectItem key={c.community_id} value={c.community_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs mb-1 block">Password</Label><Input type="password" value={formData.password || ''} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder="Leave blank for default" /></div>
            <Button onClick={handleCreateUser} disabled={formLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">{formLoading && <Loader2 size={14} className="animate-spin mr-2" />}Create User</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">Edit User</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs mb-1 block">Name</Label><Input value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} /></div>
            <div><Label className="text-xs mb-1 block">Email</Label><Input type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} /></div>
            <div><Label className="text-xs mb-1 block">Phone</Label><Input value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Role</Label>
                <Select value={formData.role || ''} onValueChange={v => setFormData({ ...formData, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resident">Resident</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Community</Label>
                <Select value={formData.community_id || 'none'} onValueChange={v => setFormData({ ...formData, community_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {communities.map(c => <SelectItem key={c.community_id} value={c.community_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs mb-1 block">Unit Number</Label><Input value={formData.unit_number || ''} onChange={e => setFormData({ ...formData, unit_number: e.target.value })} /></div>
            <Button onClick={handleUpdateUser} disabled={formLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">{formLoading && <Loader2 size={14} className="animate-spin mr-2" />}Update</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password */}
      <Dialog open={!!resetPassword} onOpenChange={(o) => { if (!o) setResetPassword(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {resetPassword?.name} ({resetPassword?.email})</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs mb-1 block">New Password *</Label><Input type="password" value={formData.password || ''} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder="Min 6 characters" /></div>
            <Button onClick={handleResetPassword} disabled={formLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">{formLoading && <Loader2 size={14} className="animate-spin mr-2" />}Update Password</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User */}
      <Dialog open={!!deleteUser} onOpenChange={(o) => { if (!o) setDeleteUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-red-600">Delete User</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="py-4"><p className="text-sm">Delete <strong>{deleteUser?.name}</strong> ({deleteUser?.email})?</p></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUser(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={formLoading}>{formLoading && <Loader2 size={14} className="animate-spin mr-2" />}Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Locked Periods Modal */}
      <Dialog open={lockedPeriodsModal.open} onOpenChange={(open) => !open && setLockedPeriodsModal({ open: false, community: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Outfit']">
              <Lock size={18} className="text-amber-500" />
              Locked Periods - {lockedPeriodsModal.community?.name}
            </DialogTitle>
            <DialogDescription>Unlock periods to allow community admin to edit/delete</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {loadingPeriods ? (
              <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>
            ) : lockedPeriods.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No locked periods found</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {lockedPeriods.map(period => (
                  <div key={period.month_label} className="border rounded-lg p-3 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{period.month_label}</p>
                      <p className="text-xs text-slate-500">
                        Locked by: {period.locked_by || 'Unknown'}
                        {period.locked_at && <span className="ml-2">on {new Date(period.locked_at).toLocaleDateString('en-IN')}</span>}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-8 gap-1 text-amber-600 border-amber-200 hover:bg-amber-50"
                      onClick={() => handleUnlockPeriod(period.month_label)}
                      disabled={formLoading}
                    >
                      {formLoading ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={14} />}
                      Unlock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockedPeriodsModal({ open: false, community: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
